import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import FormField from '../ui/FormField';
import Input from '../ui/Input';
import Select from '../ui/Select';
import { useT } from '../../i18n/LocaleContext';
import { useApi } from '../../api/client';
import { listRoles } from '../../api/roles';

const MOCK_ROLES = ['admin', 'tresorier', 'coordinateur', 'secretaire'];

const emptyForm = {
  firstName: '',
  lastName: '',
  username: '',
  email: '',
  role: '',     // mock: role name; api: roleId (uuid)
  phone: '',
  password: '',
  passwordConfirm: '',
};

export default function UserFormModal({ isOpen, onClose, onSubmit, initial }) {
  const t = useT();
  const isEdit = Boolean(initial);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiRoles, setApiRoles] = useState([]);

  // Pull live roles from the backend the first time the modal opens. Roles
  // change rarely so a single fetch per open is fine; refetch each open
  // keeps things current after a role rename/delete in another tab.
  useEffect(() => {
    if (!isOpen || !useApi) return;
    let cancelled = false;
    listRoles()
      .then((roles) => { if (!cancelled) setApiRoles(roles || []); })
      .catch(() => { if (!cancelled) setApiRoles([]); });
    return () => { cancelled = true; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (initial) {
      setForm({
        firstName: initial.firstName || '',
        lastName: initial.lastName || '',
        username: initial.username || '',
        email: initial.email || '',
        role: useApi ? (initial.roleId || '') : (initial.role || 'coordinateur'),
        phone: initial.phone || '',
        password: '',
        passwordConfirm: '',
      });
    } else {
      setForm({
        ...emptyForm,
        role: useApi ? '' : 'coordinateur',
      });
    }
    setErrors({});
  }, [isOpen, initial]);

  // Default to the first available role once API roles arrive, when creating.
  useEffect(() => {
    if (!useApi || !isOpen || isEdit) return;
    if (form.role || apiRoles.length === 0) return;
    setForm((prev) => ({ ...prev, role: apiRoles[0].id }));
  }, [apiRoles, isOpen, isEdit, form.role]);

  const set = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const roleOptions = useMemo(() => {
    if (useApi) {
      return apiRoles.map((r) => ({ value: r.id, label: r.name }));
    }
    return MOCK_ROLES.map((r) => ({ value: r, label: t(`users.roles.${r}`) }));
  }, [t, apiRoles]);

  const validate = () => {
    const next = {};
    if (!form.firstName.trim()) next.firstName = t('validation.required');
    if (!form.lastName.trim()) next.lastName = t('validation.required');
    if (!form.username.trim()) next.username = t('validation.required');
    if (!form.phone.trim()) next.phone = t('validation.required');
    if (!form.role) next.role = t('validation.required');
    if (!isEdit) {
      if (!form.password || form.password.length < 6) next.password = t('validation.min_length', { count: 6 });
    } else if (form.password && form.password.length < 6) {
      next.password = t('validation.min_length', { count: 6 });
    }
    if ((form.password || form.passwordConfirm) && form.password !== form.passwordConfirm) {
      next.passwordConfirm = t('users.password_mismatch');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        firstName: form.firstName,
        lastName: form.lastName,
        username: form.username,
        email: form.email || null,
        phone: form.phone,
      };
      if (useApi) {
        payload.roleId = form.role;
      } else {
        payload.role = form.role;
      }
      if (form.password) payload.password = form.password;
      await onSubmit(payload);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={submitting ? () => {} : onClose}
      title={isEdit ? t('users.edit_user') : t('users.new_user')}
      size="md"
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>{t('common.cancel')}</Button>
          <Button variant="gold" onClick={handleSubmit} loading={submitting}>
            {isEdit ? t('common.save') : t('common.create')}
          </Button>
        </>
      )}
    >
      <form onSubmit={handleSubmit} noValidate>
        <div className="form-row form-row--2">
          <FormField label={t('members.field_first_name')} htmlFor="firstName" required error={errors.firstName}>
            <Input id="firstName" value={form.firstName} onChange={set('firstName')} invalid={Boolean(errors.firstName)} autoFocus />
          </FormField>
          <FormField label={t('members.field_last_name')} htmlFor="lastName" required error={errors.lastName}>
            <Input id="lastName" value={form.lastName} onChange={set('lastName')} invalid={Boolean(errors.lastName)} />
          </FormField>
        </div>
        <div className="form-row form-row--2">
          <FormField label={t('users.field_username')} htmlFor="username" required error={errors.username}>
            <Input id="username" value={form.username} onChange={set('username')} invalid={Boolean(errors.username)} autoComplete="off" disabled={isEdit} />
          </FormField>
          <FormField label={t('users.field_role')} htmlFor="role" required error={errors.role}>
            <Select
              id="role"
              value={form.role}
              onChange={set('role')}
              options={roleOptions}
              placeholder={useApi ? t('common.select_placeholder') : undefined}
              invalid={Boolean(errors.role)}
            />
          </FormField>
        </div>
        <div className="form-row form-row--2">
          <FormField label={t('members.field_email')} htmlFor="email" hint={t('common.optional')}>
            <Input id="email" type="email" value={form.email} onChange={set('email')} autoComplete="off" />
          </FormField>
          <FormField label={t('users.field_phone')} htmlFor="phone" required error={errors.phone}>
            <Input id="phone" value={form.phone} onChange={set('phone')} invalid={Boolean(errors.phone)} placeholder="+250 7..." />
          </FormField>
        </div>
        <div className="form-row form-row--2">
          <FormField
            label={t('users.field_password')}
            htmlFor="password"
            required={!isEdit}
            error={errors.password}
            hint={isEdit ? t('users.password_hint') : undefined}
          >
            <Input id="password" type="password" value={form.password} onChange={set('password')} invalid={Boolean(errors.password)} autoComplete="new-password" />
          </FormField>
          <FormField label={t('users.field_password_confirm')} htmlFor="passwordConfirm" error={errors.passwordConfirm}>
            <Input id="passwordConfirm" type="password" value={form.passwordConfirm} onChange={set('passwordConfirm')} invalid={Boolean(errors.passwordConfirm)} autoComplete="new-password" />
          </FormField>
        </div>
      </form>
    </Modal>
  );
}
